import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../Store/Store'

// Types based on simplified box-only backend
export type Card = { id: string; front: string; box: number; nextReviewAt?: string; lastReviewedAt?: string }
export type CardsResponse = { items: Card[]; nextCursor?: string | null }
export type PlanResponse = { date: string; dueCount: number; byDeck: { box: number; deckName: string; count: number }[] }
export type DueResponse = { dueCount: number; items: Card[] }
export type ReviewResult = { id: string; nextReviewAt: string; lastReviewedAt: string; box: number; learned?: boolean }
export type OverviewResponse = { range: string; totalReviews: number; accuracy: number; streakDays: number; perDeck: { box: number; name: string; reviews: number; accuracy: number }[] }
export type ConfigResponse = { reviewNumber: number }
export type LearnedItem = { id: string; word: string; learnedAt: string; boxName?: string }

export const leitnerApi = createApi({
  reducerPath: 'leitnerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['card','due','plan','config','stats'],
  endpoints: (builder) => ({
    getCards: builder.query<CardsResponse, { box?: number; q?: string; limit?: number; cursor?: string }>({
      query: (p) => {
        const qs = new URLSearchParams()
        if (typeof p?.box === 'number') qs.set('box', String(p.box))
        if (p?.q) qs.set('q', p.q)
        qs.set('limit', String(p?.limit ?? 50))
        if (p?.cursor) qs.set('cursor', p.cursor)
        return `/cards?${qs.toString()}`
      },
      providesTags: ['card']
    }),
    createCard: builder.mutation<Card, { front: string; box?: number; examQuestionId?: number }>({
      query: (body) => ({ url: '/cards', method: 'POST', body }),
      invalidatesTags: ['card']
    }),
    updateCard: builder.mutation<Card, { id: string; front?: string }>({
      query: ({ id, ...patch }) => ({ url: `/cards/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: ['card']
    }),
    deleteCard: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/cards/${id}`, method: 'DELETE' }),
      invalidatesTags: ['card']
    }),
    importCards: builder.mutation<{ created: number; duplicates?: number }, { box?: number; items: { front: string }[] }>({
      query: (body) => ({ url: '/cards/import', method: 'POST', body }),
      invalidatesTags: ['card']
    }),
    getLearned: builder.query<LearnedItem[], void>({
      query: () => '/cards/learned',
      providesTags: ['card']
    }),

    getDue: builder.query<DueResponse, { box?: number; limit?: number; now?: string }>({
      query: (p) => {
        const qs = new URLSearchParams()
        if (typeof p?.box === 'number') qs.set('box', String(p.box))
        if (p?.limit) qs.set('limit', String(p.limit))
        if (p?.now) qs.set('now', p.now)
        return `/reviews/due?${qs.toString()}`
      },
      providesTags: ['due']
    }),
    reviewCard: builder.mutation<ReviewResult, { cardId: string; correct: boolean }>({
      query: ({ cardId, correct }) => ({ url: `/reviews/${cardId}`, method: 'POST', body: { correct } }),
      invalidatesTags: ['due','stats']
    }),
    getPlan: builder.query<PlanResponse, { date: string }>({
      query: ({ date }) => `/reviews/plan?date=${date}`,
      providesTags: ['plan']
    }),
    getOverview: builder.query<OverviewResponse, { range?: string }>({
      query: ({ range = '30d' } = {}) => `/stats/overview?range=${range}`,
      providesTags: ['stats']
    }),
    getConfig: builder.query<ConfigResponse, void>({ query: () => '/leitner/config', providesTags: ['config'] }),
    updateConfig: builder.mutation<{ success: boolean }, { reviewNumber: number }>({
      query: (body) => ({ url: '/leitner/config', method: 'PATCH', body }),
      invalidatesTags: ['config']
    }),
  })
})

export const {
  useGetCardsQuery,
  useCreateCardMutation,
  useUpdateCardMutation,
  useDeleteCardMutation,
  useImportCardsMutation,
  useGetLearnedQuery,
  useGetDueQuery,
  useReviewCardMutation,
  useGetPlanQuery,
  useGetOverviewQuery,
  useGetConfigQuery,
  useUpdateConfigMutation,
} = leitnerApi
